<script setup>
import { useI18n } from 'vue-i18n';
import { formatBRL } from 'dashboard/helper/lais/currency';
import {
  presentSupportAddress,
  presentSupportPersonName,
} from '../composables/realEstateCommissionSupportRowPresenter';
import RealEstateCommissionSupportPersonChip from './RealEstateCommissionSupportPersonChip.vue';
import RealEstateCommissionSupportRowActions from './RealEstateCommissionSupportRowActions.vue';

const props = defineProps({
  row: { type: Object, required: true },
  tab: { type: String, default: 'para_enviar' },
  actionsVisible: { type: Boolean, default: false },
});

const emit = defineEmits(['open', 'details', 'hover']);
const { t } = useI18n();
const fallback = () => t('REAL_ESTATE_COMMISSION_SUPPORT.TABLE.NOT_INFORMED');
const money = value => {
  const normalized = value?.amount ?? value;
  if (typeof normalized === 'string' && normalized.trim().startsWith('R$')) {
    return normalized;
  }
  return formatBRL(normalized, { fallback: fallback() });
};
const person = value => presentSupportPersonName(value) || fallback();
const date = value => value || fallback();
const address = () =>
  presentSupportAddress(props.row).main ||
  t('REAL_ESTATE_COMMISSION_SUPPORT.TABLE.ADDRESS_UNAVAILABLE');
const addressDetail = () => presentSupportAddress(props.row).detail;
const openPrimary = () =>
  emit(props.tab === 'enviadas' ? 'details' : 'open', props.row);
</script>

<template>
  <tr
    class="h-[90px] border-t border-[#E0E7EB] align-top font-[Roboto] text-base text-[#5D696F] transition hover:bg-[#F7FBFB]"
    :class="
      tab === 'para_enviar' && row.status_visualizacao_atual === 0
        ? '[&_p]:font-bold'
        : ''
    "
    @mouseenter="emit('hover', row.processo_id)"
  >
    <td
      class="border-l-[3px] p-0 align-top"
      :class="actionsVisible ? 'border-[#85E0D9]' : 'border-white'"
    >
      <div class="h-full px-2 py-4 min-[1401px]:px-[1vw]">
        <div class="flex w-[79px] justify-center">
          <RealEstateCommissionSupportPersonChip
            :name="person(row.pos_venda_name)"
          />
        </div>
      </div>
    </td>
    <td class="p-0 align-top">
      <div class="h-full px-2 py-4 min-[1401px]:px-[1vw]">
        <div class="flex w-[90px] flex-col items-start gap-2">
          <span>{{ date(row.data_entrada) }}</span>
          <span class="text-sm font-normal text-[#74848B]">
            {{ row.hora_entrada || '' }}
          </span>
        </div>
      </div>
    </td>
    <td class="p-0 align-top">
      <div class="h-full px-2 py-4 min-[1401px]:px-[1vw]">
        <div class="flex w-[92px] flex-col items-start">
          {{ row.loja_name || fallback() }}
        </div>
      </div>
    </td>
    <td class="p-0 align-top">
      <div class="h-full px-2 py-4 min-[1401px]:px-[1vw]">
        <button
          type="button"
          class="flex w-[15.7vw] flex-col items-start !rounded-none !p-0 font-[Roboto] text-base leading-[22.88px] text-left hover:text-[#01988C]"
          @click="openPrimary"
        >
          <p class="m-0">{{ address() }}</p>
          <span
            v-if="addressDetail()"
            class="block text-[13.6px] font-normal leading-[19.448px] text-[#74848B]"
          >
            {{ addressDetail() }}
          </span>
        </button>
      </div>
    </td>
    <td class="p-0 align-top">
      <div class="h-full px-2 py-4 min-[1401px]:px-[1vw]">
        <div
          class="flex w-[7vw] flex-col items-start max-[1400px]:w-[calc(7vw+2px)]"
        >
          <p class="m-0">{{ money(row.valor_comissao) }}</p>
        </div>
      </div>
    </td>
    <td class="p-0 align-top">
      <div class="h-full px-2 py-4 min-[1401px]:px-[1vw]">
        <div class="flex w-[8vw] flex-col items-start">
          <p class="m-0">{{ money(row.valor_venda) }}</p>
        </div>
      </div>
    </td>
    <td class="p-0 align-top">
      <div class="h-full px-2 py-4 min-[1401px]:px-[1vw]">
        <div class="flex w-[90px] flex-col items-start gap-2">
          <span>{{ date(row.data_assinatura) }}</span>
          <span class="text-sm font-normal text-[#74848B]">
            {{ row.hora_assinatura || '' }}
          </span>
        </div>
      </div>
    </td>
    <td class="p-0 align-top">
      <div class="h-full px-2 py-4 min-[1401px]:px-[1vw]">
        <div class="flex w-[79px] justify-center">
          <RealEstateCommissionSupportPersonChip
            :name="person(row.gerente_name)"
          />
        </div>
      </div>
    </td>
    <td
      data-testid="commission-support-type-cell"
      class="p-0 align-top"
      :class="tab === 'enviadas' ? 'bg-[#E7F4D7]' : ''"
    >
      <div class="h-full px-2 py-4 min-[1401px]:px-[1vw]">
        <div :class="tab === 'enviadas' ? 'w-[90px]' : 'w-[75px]'">
          <span v-if="tab === 'enviadas'" class="font-normal text-[#6B9539]">
            {{ date(row.datas_parcelas) }}
          </span>
          <template v-else>
            <span>
              {{
                row.comissao_incompleta
                  ? t('REAL_ESTATE_COMMISSION_SUPPORT.TABLE.INCOMPLETE_TYPE')
                  : row.tipo_venda
              }}
            </span>
            <span
              v-if="row.quantidade_parcela > 1"
              class="mt-1 block text-sm font-normal text-[#74848B]"
            >
              {{
                t('REAL_ESTATE_COMMISSION_SUPPORT.TABLE.INSTALLMENTS', {
                  count: row.quantidade_parcela,
                })
              }}
            </span>
          </template>
        </div>
      </div>
    </td>
    <td v-if="tab !== 'enviadas'" class="p-0 text-center align-top">
      <div class="h-full py-4">
        <div class="w-[75px]">
          <span
            v-if="row.comissao_incompleta"
            class="rounded-[4px] bg-[#FDEBEC] px-2 py-1 text-[10px] font-normal uppercase text-[#B4232F]"
          >
            {{ t('REAL_ESTATE_COMMISSION_SUPPORT.TABLE.INCOMPLETE') }}
          </span>
          <span
            v-else-if="row.status_visualizacao_atual === 0"
            class="rounded-[4px] bg-[#E7F4D7] px-2 py-1 text-[10px] font-normal uppercase text-[#6B9539]"
          >
            {{ t('REAL_ESTATE_COMMISSION_SUPPORT.TABLE.NEW') }}
          </span>
        </div>
      </div>
    </td>
    <td class="p-0 text-right align-top">
      <div class="h-full px-2 py-4 min-[1401px]:px-[1vw]">
        <div class="flex w-[65px] justify-center">
          <RealEstateCommissionSupportRowActions
            :visible="actionsVisible"
            @open="emit('open', row)"
            @details="emit('details', row)"
          />
        </div>
      </div>
    </td>
  </tr>
</template>
